// Browser notification layer. Owns permission and the Notification object; the
// "when" comes from sim/notifications.ts. Degrades silently when notifications
// are denied or unsupported — the game plays exactly as before.

import { bus } from '../core/events';
import type { GameState } from '../core/types';
import { t } from '../i18n';
import { nextNotification, type ScheduledNotification } from '../sim/notifications';

export class NotificationManager {
  private lastChoreHour = -1;
  private firedHatches = new Set<string>();
  private permissionRequested = false;
  private offChore: (() => void) | null = null;

  constructor(private getState: () => GameState) {}

  /**
   * Requests permission once, gently: tied to the first chore the player
   * completes, never on first paint. Safe to call repeatedly.
   */
  async requestPermission(): Promise<boolean> {
    if (typeof Notification === 'undefined') return false;
    if (this.permissionRequested) return Notification.permission === 'granted';
    this.permissionRequested = true;
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;
    const result = await Notification.requestPermission();
    return result === 'granted';
  }

  /** Hooks the one-time permission prompt to the first completed chore. */
  start(): void {
    if (typeof Notification === 'undefined') return;
    this.offChore = bus.on('CHORE_COMPLETED', () => {
      void this.requestPermission();
      this.offChore?.();
      this.offChore = null;
    });
  }

  stop(): void {
    this.offChore?.();
    this.offChore = null;
  }

  /** Fires any notification that is due at the current sim hour. Call per tick. */
  tick(): void {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;

    const state = this.getState();
    const next = nextNotification(state);
    if (!next) return;
    if (next.atHour > state.simHour) return;

    if (next.kind === 'hatch') {
      if (next.eggId && this.firedHatches.has(next.eggId)) return;
    } else if (next.atHour === this.lastChoreHour) {
      return;
    }

    this.fire(next);
  }

  private fire(next: ScheduledNotification): void {
    const title = t(`notification.${next.kind}.title`);
    const body = t(`notification.${next.kind}.body`);
    try {
      new Notification(title, { body, tag: next.kind });
    } catch {
      // Some platforms throw on the constructor; the game does not care.
    }

    if (next.kind === 'hatch' && next.eggId) this.firedHatches.add(next.eggId);
    else this.lastChoreHour = next.atHour;
  }
}
